/**
 * StoreDetailPage — a store's full "database admin" detail view.
 *
 * Mounted at /projects/:projectId/stores/:storeId as a child of ProjectLayout
 * (sidebar / header / project context come from the layout). Mirrors the
 * self-host dashboard's store detail page: a header (name + Export CSV/Delete),
 * a meta grid (ID / Type / Size / Status / Created / Documents), the optional
 * Schema block, and the ADAPTER-driven data view (StoreViewer → per-type
 * browser: sqlite SQL GUI, docs reader, file browser, dataset rows, KB search).
 *
 * Loading contract (mirrors self-host): fetch ONE store by id — never the whole
 * list — and let SIZE arrive in a SECOND, non-blocking request (`withSize=1`
 * runs a live probe whose cost grows with the data; the page must paint first).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IonIcon } from '@ionic/react';
import { copyOutline } from 'ionicons/icons';
import ConfirmModal from '../../components/molecules/ConfirmModal/ConfirmModal';
import { StoreStatus, shortStoreId, fmtBytes } from '../../components/shared/StoresTable/StoresTable';
import StoreViewer from './StoreViewer';
import { Stores } from '../../services/stores';
import { useAgents } from '../../hooks/useAgents';
import { useToast } from '../../contexts/ToastContext';
import '../PipelinesPage/PipelinesPage.css';
import './StoragePage.css';

// Store id shown compactly in the meta grid: start … end (full id on copy).
const storeIdEnds = (id) => {
  const s = String(id || '');
  return s.length <= 20 ? s : `${s.slice(0, 10)}…${s.slice(-6)}`;
};

const StoreDetailPage = () => {
  const { projectId, storeId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Resolve the owner binding (the agent rn stamped on the row at provision)
  // to a display NAME via the cached agents list — the uuid itself is never
  // shown. Cheap: React-Query dedupes with every other consumer of the list.
  const { data: agents = [] } = useAgents(projectId);

  const [store, setStore] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [noDataAccess, setNoDataAccess] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // From the deferred ?withSize=1 probe: { sizeBytes, docCount }.
  const [sizeInfo, setSizeInfo] = useState(null);
  // From the docs viewer's listing: { count, partial } (object-store types
  // count their own documents; the engine's docCount wins when reported).
  const [docMeta, setDocMeta] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Armed by the sqlite adapter with its export-CSV routine.
  const exportRef = useRef(null);

  const storesPath = `/projects/${projectId}/stores`;

  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setNoDataAccess(false);
    setSizeInfo(null);
    setDocMeta(null);
    Stores.get(projectId, storeId)
      .then((r) => {
        if (!alive) return;
        if (r && r.noDataAccess) { setNoDataAccess(true); return; }
        if (!r || !r.store) { setNotFound(true); return; }
        setStore(r.store);
        setCanWrite(r.canWrite === true);
        // SIZE is a live probe (object-store prefix sum / sidecar stat) whose
        // cost grows with the data — a second, best-effort request after paint.
        Stores.get(projectId, storeId, { withSize: true })
          .then((rs) => { if (alive && rs && rs.store) setSizeInfo({ sizeBytes: rs.store.sizeBytes, docCount: rs.store.docCount }); })
          .catch(() => { if (alive) setSizeInfo({ sizeBytes: null, docCount: null }); });
      })
      .catch((e) => {
        if (!alive) return;
        if (e && e.status === 404) setNotFound(true);
        else setError(e?.message || 'Failed to load the store');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, storeId]);

  const copyId = () => {
    navigator.clipboard.writeText(store.storeId)
      .then(() => showToast(`Copied ${storeIdEnds(store.storeId)}`, 'success'))
      .catch(() => showToast('Copy failed', 'error'));
  };

  // ConfirmModal closes itself right after onPrimary — the delete runs on and
  // reports via toasts (navigate back only once the server confirms the purge).
  const doDelete = () => {
    showToast(`Deleting ${shortStoreId(store.storeId)}…`, 'info');
    Stores.remove(projectId, store.storeId)
      .then(() => {
        showToast(`Deleted ${shortStoreId(store.storeId)}`, 'success');
        navigate(storesPath, { replace: true });
      })
      .catch((e) => showToast(`Delete failed: ${e.message}`, 'error'));
  };

  const schemaStr = store && store.schema != null
    ? (typeof store.schema === 'string' ? store.schema : JSON.stringify(store.schema, null, 2))
    : null;

  // Documents cell: prefer the engine's live docCount, else the docs viewer's
  // own listing count. Hidden until either lands.
  const docCount = sizeInfo && sizeInfo.docCount != null
    ? String(sizeInfo.docCount)
    : (docMeta ? `${docMeta.count}${docMeta.partial ? '+' : ''}` : null);

  // Loading skeleton mirroring the DETAIL layout (self-host's
  // storeDetailSkeletonHtml): title bar + action placeholder, the meta cells,
  // and shimmering content lines — the page shows its SHAPE while data loads
  // instead of a lone "Loading…" sentence.
  const detailSkeleton = (
    <div>
      <div className="store-detail-head">
        <div className="store-skel" style={{ width: 220, height: 24 }} />
        <div className="store-skel" style={{ width: 96, height: 30, borderRadius: 8 }} />
      </div>
      <div className="store-meta-grid">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="store-meta-cell">
            <div className="store-skel" style={{ width: 44, height: 9, marginBottom: 9 }} />
            <div className="store-skel" style={{ width: 84, height: 14 }} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        {[92, 100, 78, 96, 70].map((w, i) => (
          <div key={i} className="store-skel" style={{ width: `${w}%`, height: 12, marginBottom: 12 }} />
        ))}
      </div>
    </div>
  );

  const emptyState = (title, body) => (
    <div className="store-detail-empty">
      <div className="store-detail-empty-title">{title}</div>
      {body ? <div className="store-detail-empty-body">{body}</div> : null}
      <Link className="store-back-btn" to={storesPath}>Back to Storage</Link>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{store ? `${store.name || shortStoreId(store.storeId)} · Storage` : 'Storage'} · Zibby</title>
      </Helmet>

      {/* No back button — the layout breadcrumb (Projects / <project> /
          <store id>) is the way back, same as every other detail page. */}
      <div className={`pipelines-page store-detail-page${store && (store.type === 'sqlite' || store.type === 'docs' || store.type === 'postgres') ? ' store-detail-page--wide' : ''}`}>
        {loading ? (
          detailSkeleton
        ) : error ? (
          <div className="store-error" role="alert">Couldn’t load store: {error}</div>
        ) : noDataAccess ? (
          emptyState('No access to store data', 'Your token lacks Data view for this project.')
        ) : notFound ? (
          emptyState('Store not found')
        ) : store && (
          <>
            <div className="store-detail-head">
              <h1 className="pipelines-title store-detail-title">{store.name || shortStoreId(store.storeId)}</h1>
              <div className="store-detail-actions">
                {store.type === 'sqlite' && (
                  <button
                    type="button"
                    className="sv-link"
                    onClick={() => {
                      if (exportRef.current) exportRef.current();
                      else showToast('Nothing to export — open a table first.', 'warning');
                    }}
                  >
                    Export CSV
                  </button>
                )}
                <button type="button" className="store-delete-btn" onClick={() => setConfirmDelete(true)}>
                  Delete store
                </button>
              </div>
            </div>

            <div className="store-meta-grid">
              {/* The store's EXTERNAL identifier (rn — north-star #10), shown
                  in FULL — it's what a user pastes into the CLI/API, so an
                  ellipsized middle is useless (wraps inside its cell). Absent
                  on an older backend → the cell doesn't render. */}
              {store.rn ? (
                <div className="store-meta-cell store-meta-cell--rn">
                  <div className="store-meta-k">Resource ID</div>
                  <div className="store-meta-v store-meta-v--rn">
                    <span className="store-id-cell store-id-cell--rn">
                      <span>{store.rn}</span>
                      <button
                        type="button"
                        className="store-id-copy"
                        title="Copy resource id"
                        onClick={() => navigator.clipboard.writeText(store.rn)
                          .then(() => showToast('Copied resource id', 'success'))
                          .catch(() => showToast('Copy failed', 'error'))}
                      >
                        <IonIcon icon={copyOutline} />
                      </button>
                    </span>
                  </div>
                </div>
              ) : null}
              {/* Resource ID IS the identity cell; the bare storeId renders
                  only as a FALLBACK on a backend that sends no rn (the rn
                  embeds the storeId anyway). */}
              {!store.rn && (
                <div className="store-meta-cell">
                  <div className="store-meta-k">ID</div>
                  <div className="store-meta-v">
                    <span className="store-id-cell">
                      <span title={store.storeId}>{storeIdEnds(store.storeId)}</span>
                      <button type="button" className="store-id-copy" title="Copy full id" onClick={copyId}>
                        <IonIcon icon={copyOutline} />
                      </button>
                    </span>
                  </div>
                </div>
              )}
              <div className="store-meta-cell">
                <div className="store-meta-k">Type</div>
                <div className="store-meta-v">{store.typeLabel || store.type || '—'}</div>
              </div>
              <div className="store-meta-cell">
                <div className="store-meta-k">Size</div>
                <div className="store-meta-v" title={sizeInfo && sizeInfo.sizeBytes != null ? `${sizeInfo.sizeBytes} bytes` : 'measuring…'}>
                  {sizeInfo ? fmtBytes(sizeInfo.sizeBytes) : <span className="store-muted">…</span>}
                </div>
              </div>
              <div className="store-meta-cell">
                <div className="store-meta-k">Status</div>
                <div className="store-meta-v"><StoreStatus status={store.status} /></div>
              </div>
              <div className="store-meta-cell">
                <div className="store-meta-k">Created</div>
                <div className="store-meta-v">{store.createdAt ? new Date(store.createdAt).toLocaleString() : '—'}</div>
              </div>
              {docCount != null && (
                <div className="store-meta-cell">
                  <div className="store-meta-k">Documents</div>
                  <div className="store-meta-v">{docCount}</div>
                </div>
              )}
              {/* Binding attributes — read from the row (stamped at
                  provision), never derived here. Engine = which sidecar
                  serves this store; Agent = the owning agent's NAME resolved
                  from the owner rn (a raw uuid is never displayed). */}
              {store.engine ? (
                <div className="store-meta-cell">
                  <div className="store-meta-k">Engine</div>
                  <div className="store-meta-v">{store.engine}</div>
                </div>
              ) : null}
              {store.owner ? (() => {
                const ownerUuid = String(store.owner).split(':')[5] || '';
                // Stale-owner fallback: the uuid can lag a delete+redeploy
                // until re-attach — check the key spelling before concluding
                // the agent is gone (never call a live agent "detached").
                const kp = String(store.key || '').split(':');
                const kseg = kp[0] === 'agent' ? (kp[1] || '') : kp[0];
                const wf = agents.find((a) => a.uuid === ownerUuid)
                  || (kseg ? agents.find((a) => a.uuid === kseg || a.workflowType === kseg) : undefined);
                const label = wf ? (wf.displayName || wf.workflowType || 'agent') : '';
                return (
                  <div className="store-meta-cell">
                    <div className="store-meta-k">Agent</div>
                    <div className="store-meta-v">
                      {store.ownerDetachedAt
                        ? `available${label ? ` (was ${label})` : ''}`
                        : (label || 'detached agent')}
                    </div>
                  </div>
                );
              })() : null}
            </div>

            {schemaStr != null && (
              <div className="store-schema-block">
                <div className="store-section-title">Schema</div>
                <pre className="store-detail-schema">{schemaStr}</pre>
              </div>
            )}

            <div className="store-page-body">
              <StoreViewer
                store={store}
                projectId={projectId}
                canWrite={canWrite}
                exportRef={exportRef}
                onDocMeta={setDocMeta}
                notify={showToast}
              />
            </div>

            {confirmDelete && (
              <ConfirmModal
                open
                onClose={() => setConfirmDelete(false)}
                title="Delete this store?"
                ariaLabel="Delete store"
                primaryLabel="Delete store"
                onPrimary={doDelete}
                secondaryLabel="Cancel"
              >
                <p className="store-delete-copy">
                  <code className="store-id-short">{store.storeId}</code> and ALL of its data will be
                  permanently deleted. Agents that declared this store will re-provision an empty one
                  on their next deploy.
                </p>
              </ConfirmModal>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default StoreDetailPage;
